/**
 * GanttRoleAssignmentTray — Slide-out panel for assigning project role slots
 * to Gantt tasks. Supports single and bulk (multi-select) assignment.
 *
 * Shows role slots grouped by phase (preparation / operational / sustainment),
 * with search filtering. Clicking a role slot assigns it to all currently
 * selected tasks.
 *
 * Epic: DEV-625 prerequisite (Role-Based Gantt Task Assignment)
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { ProjectRoleSlotResponse } from '../../api/projectRoles';
import type { TaskRoleAssignmentMap } from '../../api/ganttTaskRoleAssignments';
import type { GanttTask } from '../../api/ganttTasks';
import styles from './GanttRoleAssignmentTray.module.css';

/* ------------------------------------------------------------------ */
/*  Phase display order                                                */
/* ------------------------------------------------------------------ */

const PHASE_ORDER = ['preparation', 'operational', 'sustainment'] as const;
const PHASE_I18N: Record<string, string> = {
  preparation: 'phases.preparation',
  operational: 'phases.operational',
  sustainment: 'phases.sustainment',
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface GanttRoleAssignmentTrayProps {
  /** All role slots for the project (fetched from /api/projects/{guid}/roles). */
  roleSlots: ProjectRoleSlotResponse[];
  /** Currently selected Gantt task indices. */
  selectedIndices: Set<number>;
  /** Full task list (for resolving IDs from indices). */
  tasks: GanttTask[];
  /** Current task→role assignment map. */
  assignmentMap: TaskRoleAssignmentMap;
  /** Called when user clicks a role to assign to selected tasks. */
  onAssignRole: (roleSlotGuid: string, taskIds: string[]) => void;
  /** Called to close the tray. */
  onClose: () => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function GanttRoleAssignmentTray({
  roleSlots,
  selectedIndices,
  tasks,
  assignmentMap,
  onAssignRole,
  onClose,
}: GanttRoleAssignmentTrayProps) {
  const { t } = useAppTranslation('pages');
  const { t: tComm } = useAppTranslation('community');
  const [searchText, setSearchText] = useState('');

  // ── Derive selected task IDs ──

  const selectedTaskIds = useMemo(() => {
    const ids: string[] = [];
    for (const idx of selectedIndices) {
      const task = tasks[idx];
      if (task?.id) ids.push(task.id);
    }
    return ids;
  }, [selectedIndices, tasks]);

  // ── Filter role slots ──

  const filteredSlots = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    // Only show open/filled/partially_filled slots (exclude draft, closed)
    const active = roleSlots.filter(
      (s) => s.status !== 'draft' && s.status !== 'closed'
    );
    if (!search) return active;
    return active.filter(
      (s) =>
        s.roleLabel.toLowerCase().includes(search) ||
        s.roleCategory.toLowerCase().includes(search)
    );
  }, [roleSlots, searchText]);

  // ── Group by phase ──

  const groupedByPhase = useMemo(() => {
    const groups: Record<string, ProjectRoleSlotResponse[]> = {};
    for (const phase of PHASE_ORDER) {
      groups[phase] = [];
    }
    for (const slot of filteredSlots) {
      const phase = slot.phase || 'operational';
      if (!groups[phase]) groups[phase] = [];
      groups[phase].push(slot);
    }
    return groups;
  }, [filteredSlots]);

  // ── Count how many selected tasks already have each role ──

  const alreadyAssignedCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const taskId of selectedTaskIds) {
      const taskAssignments = assignmentMap.get(taskId.toUpperCase()) || [];
      for (const a of taskAssignments) {
        const key = a.roleSlotGuid.toUpperCase();
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    return counts;
  }, [selectedTaskIds, assignmentMap]);

  // ── Handlers ──

  const handleRoleClick = useCallback(
    (roleSlotGuid: string) => {
      if (selectedTaskIds.length === 0) return;

      // Filter out tasks that already have this role assigned
      const alreadySet = new Set<string>();
      for (const taskId of selectedTaskIds) {
        const existing = assignmentMap.get(taskId.toUpperCase()) || [];
        if (existing.some((a) => a.roleSlotGuid.toUpperCase() === roleSlotGuid.toUpperCase())) {
          alreadySet.add(taskId);
        }
      }
      const newTaskIds = selectedTaskIds.filter((id) => !alreadySet.has(id));

      if (newTaskIds.length > 0) {
        onAssignRole(roleSlotGuid, newTaskIds);
      }
    },
    [selectedTaskIds, assignmentMap, onAssignRole]
  );

  return (
    <div className={styles.tray}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.title}>{t('gantt.rolesTrayTitle')}</span>
        <button className={styles.closeBtn} onClick={onClose} title={t('close', { ns: 'common' }) || 'Close'}>
          ✕
        </button>
      </div>

      {/* Search */}
      <div className={styles.searchWrapper}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder={t('gantt.rolesTraySearch')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          autoFocus
        />
      </div>

      {/* Selected count */}
      {selectedTaskIds.length > 0 && (
        <div className={styles.selectedInfo}>
          {t('gantt.bulkAssignCount', { count: selectedTaskIds.length })}
        </div>
      )}

      {/* Role list grouped by phase */}
      <div className={styles.roleList}>
        {filteredSlots.length === 0 ? (
          <div className={styles.emptyState}>
            {searchText ? 'No matching roles' : t('gantt.noRolesAssigned')}
          </div>
        ) : (
          PHASE_ORDER.map((phase) => {
            const slots = groupedByPhase[phase];
            if (!slots || slots.length === 0) return null;
            return (
              <div key={phase} className={styles.phaseGroup}>
                <div className={styles.phaseLabel}>
                  {tComm(PHASE_I18N[phase]) || phase}
                </div>
                {slots.map((slot) => {
                  const alreadyCount = alreadyAssignedCounts.get(slot.roleSlotGuid.toUpperCase()) || 0;
                  return (
                    <button
                      key={slot.roleSlotGuid}
                      className={styles.roleItem}
                      onClick={() => handleRoleClick(slot.roleSlotGuid)}
                      disabled={selectedTaskIds.length === 0}
                    >
                      <div className={styles.roleItemContent}>
                        <div className={styles.roleLabel}>{slot.roleLabel}</div>
                        <div className={styles.roleMeta}>
                          <span className={styles.categoryTag}>
                            {tComm(`categories.${slot.roleCategory}`) || slot.roleCategory}
                          </span>
                          <span className={styles.fillStatus}>
                            {t('gantt.filledStatus', {
                              filled: slot.slotsFilled,
                              needed: slot.slotsNeeded,
                            })}
                          </span>
                        </div>
                      </div>
                      {alreadyCount > 0 && (
                        <span className={styles.alreadyCount} title={t('gantt.alreadyAssigned', { count: alreadyCount })}>
                          {alreadyCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
